import React, { useState, useRef, useEffect, useMemo } from 'react'
import {
	FilterAlt,
	Close,
	Info,
	RadioButtonChecked,
	RadioButtonUnchecked,
} from '@mui/icons-material'
import { Controller, useForm } from 'react-hook-form'
import dayjs from 'dayjs'
import { useMediaQuery } from 'react-responsive'
import { ClassNameValue } from 'tailwind-merge'
import DatePicker from '../ui/DatePicker'
import cn from '../../lib/utils/cn'
import {
	IconButton,
	Input,
	Select,
	Button,
	MultiValueInput,
	Tooltip,
} from '../ui'
import {
	ColumnFilterType,
	CustomColumnFiltersState,
} from '../../pages/Workspace/components/interfaces.table'
import {
	FILTER_TYPES,
	TEXT_FILTER_OPTIONS,
	NUMERIC_FILTER_OPTIONS,
} from '../../hooks/table/useColumnFilters'
import { IconButtonProps } from '../ui/IconButton'
import Checkbox from '../ui/Checkbox'
import {
	useOrganizationCategories,
	useOrganizationUsers,
} from '../../hooks/api/organizationHooks'
import { MainLayoutType } from '../layouts/MainLayout'
import { useOutletContext } from 'react-router-dom'
import { useAuth } from '../../hooks'

export const FILTERED_OBJECT_TYPE_VALUES = [
	'name',
	'user',
	'category',
	'subcategory',
	'type',
] as const

export type FilteredObjectType =
	| 'name'
	| 'user'
	| 'category'
	| 'subcategory'
	| 'type'

interface FilterButtonProps {
	columnKey: string
	setColumnFilters: (filters: CustomColumnFiltersState) => void
	columnFilters: CustomColumnFiltersState
	isDate?: boolean
	isSize?: boolean
	className?: ClassNameValue
	buttonConfig?: IconButtonProps
	filterWindowClassName?: ClassNameValue
	selectedFilterType?: []
	withoutTypeSelector?: boolean
	multiValueItems?: { id: string | number; label: string }[] | null
	extraMultiValueItems?: { id: string; label: string }[] | null
	checkboxesItems?: { id: string | number; label: string }[] | null
	objectType?: FilteredObjectType | null
	inputLabel?: string
	inputLabelClassname?: string
	initialFilterType?: ColumnFilterType | null
	isActiveIndicator?: boolean
}

const FilterButton: React.FC<FilterButtonProps> = ({
	columnKey,
	setColumnFilters,
	columnFilters,
	isDate = false,
	isSize = false,
	className,
	buttonConfig,
	filterWindowClassName,
	withoutTypeSelector = false,
	multiValueItems: propsMultiValueItems = null,
	extraMultiValueItems = null,
	checkboxesItems = null,
	objectType,
	inputLabel = '',
	inputLabelClassname = '',
	initialFilterType,
	isActiveIndicator = false,
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const containerRef = useRef<HTMLDivElement>(null)
	const { isAuthenticated, currentUser } = useAuth()
	const { data: usersData, isLoading: isLoadingUsers } = useOrganizationUsers(
		{ id: currentUser?.organization?.id ?? ''},
		{
			enabled: Boolean(
				isAuthenticated &&
				currentUser?.organization?.id &&
				isOpen &&
				objectType === 'user'
			),
			staleTime: 1000 * 120,
		}
	)

	const { data: categoriesData, isLoading: isLoadingCategories } =
		useOrganizationCategories(
			{ id: currentUser?.organization?.id ?? ''},
			{
				enabled: Boolean(
					isAuthenticated &&
					currentUser?.organization?.id &&
					isOpen &&
					objectType &&
					['category', 'subcategory']?.includes(objectType)
				),
				staleTime: 1000 * 120,
			}
		)

	const [multiValueItems, setMultiValueItems] = useState<
		{ id: string; label: string }[] | null
	>(
		propsMultiValueItems
			? propsMultiValueItems.map((i) => ({ id: String(i.id), label: i.label }))
			: null
	)

	useEffect(() => {
		if (!isOpen) return

		// If propsMultiValueItems are provided, use them directly
		if (propsMultiValueItems?.length) {
			setMultiValueItems(
				propsMultiValueItems?.map((i) => ({
					id: String(i.id),
					label: i.label,
				}))
			)
			return
		}

		switch (objectType) {
			case 'user': {
				setMultiValueItems([
					...(extraMultiValueItems ? extraMultiValueItems : []),
					...(usersData?.data?.entries
						? usersData.data.entries.map(({ id, name, email }) => ({
							id: String(id),
							label: name || email,
						}))
						: []),
				])
				break
			}
			case 'category': {
				setMultiValueItems([
					...(extraMultiValueItems ? extraMultiValueItems : []),
					...(categoriesData?.data?.entries
						? categoriesData.data.entries
							?.filter((c) => !c?.parentId)
							?.map(({ id, friendlyName }) => ({
								id: String(id),
								label: friendlyName,
							}))
						: []),
				])
				break
			}
			case 'subcategory': {
				const selectedCategories = columnFilters?.find(
					(f) => f?.id === 'category'
				)?.value
				setMultiValueItems([
					...(extraMultiValueItems ? extraMultiValueItems : []),
					...(categoriesData?.data?.entries
						? categoriesData.data.entries
							?.filter((c) => {
								if (!c?.parentId) return false
								if (!selectedCategories) return true
								const selectedIds =
									typeof selectedCategories === 'string'
										? selectedCategories.split(',').map(String)
										: Array.isArray(selectedCategories)
											? selectedCategories.map(String)
											: []
								return selectedIds.includes(String(c.parentId))
							})
							?.map(({ id, friendlyName }) => ({
								id: String(id),
								label: friendlyName,
							}))
						: []),
				])
				break
			}
			default: {
				// No specific objectType handling needed
				break
			}
		}
	}, [
		objectType,
		propsMultiValueItems,
		extraMultiValueItems,
		usersData,
		categoriesData,
		isOpen,
	])

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as HTMLElement
			if (
				containerRef.current?.contains(target) ||
				target?.closest('.select-dropdown-container') ||
				target?.closest('.select-dropdown-option')
			) {
				return
			}
			setIsOpen(false)
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const existingFilter = columnFilters?.find((f) => f.id === columnKey)
	const placeholder = useMemo(() => {
		switch (objectType) {
			case 'name':
				return 'File name'
			case 'type':
				return 'PDF, DOCX...'
			default:
				return ''
		}
	}, [objectType])

	const { watch, control, reset, setValue, trigger } = useForm<{
		type: ColumnFilterType
		value: string | number | Date | string[]
		startValue?: string | number | Date
		endValue?: string | number | Date
		unit?: string | null
	}>({
		defaultValues: {
			type: initialFilterType
				? initialFilterType
				: typeof existingFilter?.type === 'number'
					? existingFilter.type
					: isSize || isDate
						? FILTER_TYPES.LESS_THAN
						: FILTER_TYPES.CONTAINS,
			value: multiValueItems
				? Array.isArray(existingFilter?.value)
					? existingFilter?.value.map(String)
					: typeof existingFilter?.value === 'string' &&
						existingFilter?.value.length > 0
						? existingFilter.value.split(',').map(String)
						: []
				: isSize
					? existingFilter?.value && !isNaN(Number(existingFilter.value))
						? Number(existingFilter.value) /
						(existingFilter?.unit === 'mb' ? 1024 * 1024 : 1024)
						: ''
					: ((existingFilter?.value ?? '') as string | number),
			startValue: isSize
				? existingFilter?.value &&
					typeof existingFilter.value === 'object' &&
					'startValue' in existingFilter.value &&
					!isNaN(Number(existingFilter.value.startValue))
					? Number(existingFilter.value.startValue) /
					(existingFilter?.unit === 'mb' ? 1024 * 1024 : 1024)
					: ''
				: existingFilter?.value &&
					typeof existingFilter.value === 'object' &&
					'startValue' in existingFilter.value
					? existingFilter.value.startValue
					: '',
			endValue: isSize
				? existingFilter?.value &&
					typeof existingFilter.value === 'object' &&
					'endValue' in existingFilter.value &&
					!isNaN(Number(existingFilter.value.endValue))
					? Number(existingFilter.value.endValue) /
					(existingFilter?.unit === 'mb' ? 1024 * 1024 : 1024)
					: ''
				: existingFilter?.value &&
					typeof existingFilter.value === 'object' &&
					'endValue' in existingFilter.value
					? existingFilter.value.endValue
					: '',
			unit: isSize ? existingFilter?.unit || 'kb' : null,
		},
	})

	useEffect(() => {
		trigger()
	}, [existingFilter])

	const data = watch()

	const onSubmit = (data: {
		type: ColumnFilterType
		value: string | number | Date | string[]
		startValue?: string | number | Date
		endValue?: string | number | Date
		unit?: string | null
	}) => {
		// Helper: build a stable key from filters to detect no-op changes
		const normalizeFilters = (filters: CustomColumnFiltersState) =>
			(filters || [])
				.map((f) => ({
					id: f.id,
					type: f.type,
					value:
						typeof f.value === 'object' && f.value !== null
							? JSON.stringify(f.value)
							: Array.isArray(f.value)
								? f.value.join(',')
								: String(f.value ?? ''),
					unit: f.unit || undefined,
				}))
				.sort((a, b) => {
					const ak = `${a.id}|${a.type}|${a.value}|${a.unit ?? ''}`
					const bk = `${b.id}|${b.type}|${b.value}|${b.unit ?? ''}`
					return ak.localeCompare(bk)
				})
				.map((f) => `${f.id}|${f.type}|${f.value}|${f.unit ?? ''}`)
				.join(';')

		let newFilters =
			data?.value || data?.startValue || data?.endValue
				? [
					...columnFilters.filter((f) => f.id !== columnKey),
					{
						id: columnKey,
						type: data?.type,
						value:
							data?.type === FILTER_TYPES.BETWEEN &&
								data?.startValue &&
								data?.endValue
								? {
									startValue: isSize
										? Number(data.startValue) *
										(data.unit === 'mb' ? 1024 * 1024 : 1024)
										: isDate
											? !Array.isArray(data.startValue) &&
												dayjs(data.startValue).isValid()
												? dayjs(data.startValue).format('YYYY-MM-DD')
												: data.startValue
											: data.startValue,
									endValue: isSize
										? Number(data.endValue) *
										(data.unit === 'mb' ? 1024 * 1024 : 1024)
										: isDate
											? !Array.isArray(data.endValue) &&
												dayjs(data.endValue).isValid()
												? dayjs(data.endValue).format('YYYY-MM-DD')
												: data.endValue
											: data.endValue,
								}
								: isSize
									? Number(data.value) *
									(data.unit === 'mb' ? 1024 * 1024 : 1024)
									: isDate &&
										!Array.isArray(data.value) &&
										dayjs(data.value).isValid()
										? dayjs(data.value).format('YYYY-MM-DD')
										: data?.value,
						unit: isSize ? data?.unit || 'kb' : undefined,
					},
				]
				: columnFilters.filter((f) => f.id !== columnKey)

		// remove sub-categories filters if we changed category's one
		if (objectType === 'category') {
			const categoryFilterValue = newFilters.find(
				(f) => f.id === 'category'
			)?.value
			const subcategoryFilter = newFilters.find((f) => f.id === 'subcategory')

			const categoriesIds =
				typeof categoryFilterValue === 'string'
					? categoryFilterValue?.split(',').map(String)
					: []

			if (subcategoryFilter && categoriesData?.data?.entries) {
				const subcategoriesIds =
					typeof subcategoryFilter.value === 'string'
						? subcategoryFilter.value?.split(',').map(String)
						: []

				const validSubcategoriesIds = subcategoriesIds.filter((id) => {
					const subcategory = categoriesData.data.entries.find(
						(c) => String(c.id) === id
					)
					return (
						subcategory && categoriesIds.includes(String(subcategory?.parentId))
					)
				})

				if (validSubcategoriesIds.length) {
					newFilters = newFilters?.map((f) =>
						f?.id === 'subcategory'
							? { ...f, value: validSubcategoriesIds.join(',') }
							: f
					)
				} else {
					newFilters = newFilters?.filter((f) => f?.id !== 'subcategory')
				}
			}
		}

		// No-op guard: do not update if filters did not effectively change
		const prevKey = normalizeFilters(columnFilters)
		const nextKey = normalizeFilters(newFilters as CustomColumnFiltersState)
		if (prevKey === nextKey) {
			setIsOpen(false)
			return
		}

		setColumnFilters(newFilters as CustomColumnFiltersState)
		setIsOpen(false)
	}

	const renderDateFields = () =>
		data?.type === FILTER_TYPES.BETWEEN ? (
			<>
				<Controller
					control={control}
					name="startValue"
					render={({ field: { value, onChange } }) => (
						<DatePicker
							value={
								Array.isArray(value)
									? null
									: value
										? dayjs(value).toDate()
										: null
							}
							onChange={(newValue) => {
								onChange(newValue)
								if (
									newValue &&
									data.endValue &&
									!Array.isArray(data.endValue)
								) {
									const startDate = dayjs(newValue)
									const endDate = dayjs(data.endValue)
									if (startDate.isAfter(endDate)) {
										setValue('endValue', '')
									}
								}
							}}
							placeholder="Start Date"
							className="w-full dark:text-text-main-dark dark:bg-background-dark"
						// maxDate={
						// 	Array.isArray(data.endValue)
						// 		? undefined
						// 		: data.endValue
						// 		? dayjs(data.endValue).toDate()
						// 		: undefined
						// }
						/>
					)}
				/>
				<Controller
					control={control}
					name="endValue"
					render={({ field: { value, onChange } }) => (
						<DatePicker
							value={
								Array.isArray(value)
									? null
									: value
										? dayjs(value).toDate()
										: null
							}
							onChange={(newValue) => {
								onChange(newValue)
								if (
									newValue &&
									data.startValue &&
									!Array.isArray(data.startValue)
								) {
									const startDate = dayjs(data.startValue)
									const endDate = dayjs(newValue)
									if (endDate.isBefore(startDate)) {
										setValue('startValue', '')
									}
								}
							}}
							placeholder="End Date"
							className="w-full dark:text-text-main-dark dark:bg-background-dark"
						// minDate={
						// 	Array.isArray(data.startValue)
						// 		? undefined
						// 		: data.startValue
						// 		? dayjs(data.startValue).toDate()
						// 		: undefined
						// }
						/>
					)}
				/>
			</>
		) : (
			<Controller
				control={control}
				name="value"
				render={({ field: { value, onChange } }) => (
					<DatePicker
						value={
							Array.isArray(value) ? null : value ? dayjs(value).toDate() : null
						}
						onChange={onChange}
						placeholder="Pick a date"
						className=""
					/>
				)}
			/>
		)

	const renderSizeFields = () => {
		return (
			<div className="flex flex-col gap-3">
				{data?.type === FILTER_TYPES.BETWEEN ? (
					<>
						<Controller
							control={control}
							name="startValue"
							render={({ field: { value, onChange } }) => (
								<Input
									onChange={onChange}
									onBlur={(e) => {
										if (
											data?.endValue &&
											e?.currentTarget?.value > data?.endValue
										) {
											onChange(Number(data?.endValue) - 1)
										}
										if (Number(e?.currentTarget?.value) < 0) {
											onChange(0)
										}
									}}
									value={String(value)}
									placeholder={`Min size (${data?.unit?.toUpperCase() || 'KB'
										})`}
									type="number"
									wrapperClassName="w-full h-11"
									min={0}
								/>
							)}
						/>
						<Controller
							control={control}
							name="endValue"
							render={({ field: { value, onChange } }) => (
								<Input
									onChange={onChange}
									onBlur={(e) => {
										if (
											data?.startValue &&
											e?.currentTarget?.value < data?.startValue
										) {
											onChange(Number(data?.startValue) + 1)
										}
										if (Number(e?.currentTarget?.value) < 0) {
											onChange(1)
										}
									}}
									value={value ? Number(value) : ''}
									min={1}
									placeholder={`Max size (${data?.unit?.toUpperCase() || 'KB'
										})`}
									type="number"
									wrapperClassName="w-full h-11 dark:text-text-main-dark dark:bg-background-dark"
								/>
							)}
						/>
					</>
				) : (
					<Controller
						control={control}
						name="value"
						render={({ field: { value, onChange } }) => {
							return (
								<Input
									onChange={onChange}
									onBlur={(e) => {
										if (Number(e?.currentTarget?.value) < 0) {
											onChange(1)
											return
										}
										onChange(Number(e?.currentTarget?.value))
									}}
									value={value ? Number(value) : ''}
									placeholder={`Enter size (${data?.unit?.toUpperCase() || 'KB'
										})`}
									type="number"
									wrapperClassName="w-full h-11 dark:text-text-main-dark dark:bg-background-dark"
									min={0}
								/>
							)
						}}
					/>
				)}

				<Controller
					control={control}
					name="unit"
					render={({ field: { value, onChange } }) => {
						return (
							<div className="flex items-center justify-normal gap-8">
								<p
									onClick={() => onChange('kb')}
									className="flex items-center gap-2 text-text-main dark:text-text-main-dark"
								>
									{value === 'kb' ? (
										<RadioButtonChecked className="fill-accent w-5 h-5" />
									) : (
										<RadioButtonUnchecked className="fill-text-secondary w-5 h-5" />
									)}
									KB
								</p>
								<p
									onClick={() => onChange('mb')}
									className="flex items-center gap-2 text-text-main dark:text-text-main-dark"
								>
									{value === 'mb' ? (
										<RadioButtonChecked className="fill-accent w-5 h-5" />
									) : (
										<RadioButtonUnchecked className="fill-text-secondary w-5 h-5" />
									)}
									MB
								</p>
							</div>
						)
					}}
				/>
			</div>
		)
	}

	const renderStringFields = () => {
		const onlyFolders = String(data?.value)?.toLowerCase()?.trim() === 'folder'
		const onlyFiles = String(data?.value)?.toLowerCase()?.trim() === 'file'
		return (
			<div className="flex flex-col gap-2">
				{objectType === 'type' && (
					<div className="flex items-center justify-start gap-3 w-full mb-1">
						<Checkbox
							label="Folders"
							checked={onlyFolders}
							onChange={(checked) => setValue('value', checked ? 'folder' : '')}
							labelClassName="font-normal text-sm text-text-main"
						/>
						<Checkbox
							label="Files"
							checked={onlyFiles}
							onChange={(checked) => setValue('value', checked ? 'file' : '')}
							labelClassName="font-normal text-sm text-text-main"
						/>
					</div>
				)}
				{!(objectType === 'type' && (onlyFolders || onlyFiles)) && (
					<Controller
						control={control}
						name="value"
						render={({ field: { value, onChange } }) => (
							<div>
								<Input
									onChange={onChange}
									value={String(value)}
									wrapperClassName="w-full h-11 dark:text-text-main-dark dark:bg-background-dark"
									placeholder={placeholder}
									rightIcon={
										objectType === 'type' && (
											<Tooltip
												className="w-[170px] px-0 font-normal"
												label={
													'Specify the file extension, e.g. PDF, DOC, DOCX'
												}
											>
												<Info className="fill-text-secondary w-5 h-5" />
											</Tooltip>
										)
									}
								/>
							</div>
						)}
					/>
				)}
			</div>
		)
	}

	const renderMultiValueField = () => (
		<Controller
			control={control}
			name="value"
			render={({ field: { value, onChange } }) => {
				const valueArray = (
					Array.isArray(value)
						? value.map(String)
						: typeof value === 'string' && value.length > 0
							? value.split(',').map(String)
							: []
				).filter((v) => (multiValueItems || []).some((item) => item.id === v))

				return (
					<MultiValueInput
						value={valueArray}
						onItemClick={(item) => {
							if (!valueArray.includes(item.id)) {
								onChange([...valueArray, item.id].join(','))
							}
						}}
						onDeleteItem={(id) => {
							onChange(valueArray.filter((i) => i !== id).join(','))
						}}
						items={multiValueItems || []}
						placeholder="Select..."
						label={inputLabel}
						labelClassName={inputLabelClassname}
						selectClassName="flex-1 max-h-[150px] overflow-y-auto"
						itemClassName="text-left font-medium text-text-main capitalize dark:text-text-main-dark"
						valueClassName="max-w-[177px] truncate capitalize"
						withoutKeyConfirm
						loading={isLoadingCategories || isLoadingUsers}
					/>
				)
			}}
		/>
	)

	const renderCheckboxesField = () => (
		<Controller
			control={control}
			name="value"
			render={({ field: { value, onChange } }) => {
				const valueArray = Array.isArray(value)
					? value.map(String)
					: typeof value === 'string' && value.length > 0
						? value.split(',')
						: []
				return (
					<div className="flex flex-col gap-2">
						<p className="w-full text-left text-sm text-text-secondary">
							Select status:
						</p>
						{(checkboxesItems || []).map((item) => {
							const itemId = String(item.id)
							return (
								<Checkbox
									key={itemId}
									label={item.label}
									labelClassName="font-normal text-sm text-text-main"
									checked={valueArray.includes(itemId)}
									onChange={(checked) => {
										if (checked) {
											onChange([...valueArray, itemId].join(','))
										} else {
											onChange(
												valueArray.filter((i: string) => i !== itemId).join(',')
											)
										}
									}}
								/>
							)
						})}
					</div>
				)
			}}
		/>
	)
	const isDesktop = useMediaQuery({ minWidth: 1220 })

	const isShowTypeSelector =
		!multiValueItems && !checkboxesItems && !withoutTypeSelector && !objectType

	return (
		<div
			className={cn('relative inline-block z-50', className)}
			onClick={(e) => e?.stopPropagation()}
			ref={containerRef}
		>
			<IconButton
				variant="transparent"
				className={cn(
					'w-4 h-4 hover:bg-transparent hover:text-accent-hover text-text-table dark:text-text-table-dark',
					columnFilters?.find(({ id }) => id === columnKey) &&
					'text-accent dark:text-accent'
				)}
				onClick={(e) => {
					e.stopPropagation()
					setIsOpen((prev) => !prev)
				}}
				icon={
					<FilterAlt
						className={cn(
							'w-5 h-5',
							isOpen && 'fill-accent-hover dark:fill-accent-disabled'
						)}
					/>
				}
				{...buttonConfig}
			/>
			{isActiveIndicator && (
				<div className="w-4 h-4 bg-accent rounded-full absolute -top-2 -right-2 z-50 " />
			)}

			{isOpen && (
				<div
					className={cn(
						'absolute mt-2 bg-white dark:bg-background-dark border border-stroke-dark dark:border-stroke-light-dark shadow-lg rounded-md p-3 z-50',
						isDesktop ? '-left-20 w-56' : '-left-28 w-52 md:w-44',
						isDesktop && objectType === 'type' && 'w-40',
						filterWindowClassName
					)}
				>
					<div className="flex flex-col gap-3">
						{isShowTypeSelector && (
							<div className="relative">
								<Controller
									control={control}
									name="type"
									render={({ field }) => (
										<Select
											options={
												isDate
													? NUMERIC_FILTER_OPTIONS
													: isSize
														? NUMERIC_FILTER_OPTIONS?.filter(
															(f) => f?.value !== 6
														)
														: TEXT_FILTER_OPTIONS
											}
											value={field.value}
											onChange={(e) => {
												reset({
													type: Number(e) as ColumnFilterType,
													value: '',
													startValue: '',
													endValue: '',
													unit: data?.unit,
												})
											}}
											className="w-2/3 js h-9 dark:bg-background-dark dark:text-text-main-dark"
										/>
									)}
								/>

								<IconButton
									variant="transparent"
									className="absolute -top-2 -right-2"
									onClick={() => setIsOpen(false)}
									icon={
										<Close className="w-5 h-5 fill-text-secondary dark:fill-text-main-dark" />
									}
								/>
							</div>
						)}
						{multiValueItems
							? renderMultiValueField()
							: checkboxesItems
								? renderCheckboxesField()
								: isDate
									? renderDateFields()
									: isSize
										? renderSizeFields()
										: renderStringFields()}
						<div className="flex items-center justify-end gap-2">
							<Button
								variant="secondary"
								label="Reset"
								onClick={() => {
									const resetValues = {
										type: initialFilterType
											? initialFilterType
											: isDate || isSize
												? FILTER_TYPES.LESS_THAN
												: FILTER_TYPES.CONTAINS,
										value: '',
										startValue: '',
										endValue: '',
									}
									reset(resetValues)
									onSubmit(resetValues)
								}}
							/>
							<Button
								label="Filter"
								onClick={() => onSubmit(data)}
								disabled={Boolean(
									data?.type === FILTER_TYPES.BETWEEN
										? !data?.startValue ||
										!data?.endValue ||
										(isSize && Number(data?.startValue) < 0) ||
										Number(data?.endValue) < 0
										: (isSize && Number(data?.value) < 0) ||
										(multiValueItems &&
											Array.isArray(data?.value) &&
											data?.value.length === 0)
								)}
							/>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

export default FilterButton
